import React from 'react';
import { Box } from '@mui/material';
import { COLOR } from '@src/styles/color';
import CommonInput from '@src/new-components/Input/CommonInput';
import Typography from '@src/new-components/Typography';
import LimitedFeature from '@src/components/LimitedFeature';
import Tooltip from '@src/components/Tooltip';
import ButtonLabel from '@src/new-components/Button/ButtonLabel';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import BadgeAvatar from '@src/components/BadgeAvatar';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import { convertFormatDate } from '@src/utils/date';
import moment from 'moment';
import { SUB_STATUS, VOICE_STATUS } from '@src/constants/voiceCloning';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { StyledVoicePresetDialog } from './index.style';
import SpeedNew from './Speed';

const AddSpeakerForm = ({
  values,
  errors,
  isEdit,
  user,
  onFieldChange,
  onFieldBlur,
  onOpenVoices,
  t,
}) => {
  const renderLabel = (label) => (
    <Typography variant="body-14-medium" color={COLOR.darkGray}>
      {label}
    </Typography>
  );

  const renderPlaceholder = (placeholder) => (
    <Typography variant="body-14-regular" color={COLOR.label2[24]}>
      {placeholder}
    </Typography>
  );

  const isPublicVoiceCloning = values.voice?.status === SUB_STATUS.PUBLIC;
  const isVoiceCloningAboutToRemoved =
    isPublicVoiceCloning && values.voice?.discardAt
      ? moment().isBefore(values.voice?.discardAt)
      : false;

  const voiceInActive = !values.voice?.active;
  const isInValidPublicVoiceCloning =
    (values.voice?.userId !== user?.id &&
      ((values.voice?.discardAt && moment().isAfter(values.voice?.discardAt)) ||
        values.voice?.status === VOICE_STATUS.PRIVATE)) ||
    voiceInActive;

  const renderVoice = () => (
    <Box display="flex" gap="8px" alignItems="center">
      <BadgeAvatar
        width={25}
        smallImgWidth={10}
        img={values.voice?.roundImage}
        smallImg={values.voice?.language?.roundImage}
        type="image"
      />
      <Typography variant="body-14-regular" color={COLOR.darkGray}>
        {values.voice?.name}
      </Typography>
      <LimitedFeature voiceFeatures={values.voice?.features} />
      {isVoiceCloningAboutToRemoved && (
        <Tooltip
          arrow
          title={t('voiceCloningDiscardAt', {
            date: convertFormatDate(values.voice?.discardAt),
          })}
          placement="top"
        >
          <RunningWithErrorsRoundedIcon
            sx={{
              color: COLOR.redPrimary,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          />
        </Tooltip>
      )}
      {isInValidPublicVoiceCloning && (
        <Tooltip arrow title={t('invalidPublicVoiceCloning')} placement="top">
          <WarningRoundedIcon
            sx={{
              color: COLOR.redPrimary,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          />
        </Tooltip>
      )}
    </Box>
  );

  const renderHelperText = (error) => (
    <Typography variant="body-12-regular" color={COLOR.functionalRed.default}>
      {error}
    </Typography>
  );

  return (
    <StyledVoicePresetDialog>
      <Box
        display="flex"
        flexDirection="row"
        gap="16px"
        alignItems="flex-start"
      >
        <CommonInput
          label={renderLabel(t('speakerName'))}
          placeholder={t('enterSpeakerName')}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '44px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: COLOR.label2[48],
            },
          }}
          name="name"
          value={values.name}
          error={errors.name}
          helperText={renderHelperText(errors.name)}
          onChange={(e) => onFieldChange('name', e.target.value)}
          onBlur={() => onFieldBlur('name')}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        gap="16px"
        alignItems="flex-start"
      >
        <ButtonLabel
          customStyle={{
            height: '44px',
            borderRadius: '8px',
            border: `1px solid ${
              errors.voice
                ? COLOR.functionalRed.default
                : COLOR.borderButtonCorlor
            }`,
            width: '100%',
            justifyContent: 'space-between',
            padding: '0 16px',
            alignItems: 'center',
            gap: '8px',
            '&:focus': {
              backgroundColor: 'transparent',
            },
            '&:hover': {
              border: `1px solid ${
                errors.voice
                  ? COLOR.functionalRed.default
                  : COLOR.borderButtonCorlor
              }`,
            },
          }}
          onClick={onOpenVoices}
          variant="outlined"
          label={renderLabel(t('voice'))}
          error={errors.voice}
          helperText={renderHelperText(errors.voice)}
        >
          {values.voice?.code
            ? renderVoice()
            : renderPlaceholder(t('selectVoice'))}
          <ArrowRightRoundedIcon
            sx={{ color: COLOR.indigo[80], fontSize: '24px' }}
          />
        </ButtonLabel>
        <SpeedNew
          currentSpeed={values.speed}
          onChange={(value) => onFieldChange('speed', value)}
        />
      </Box>
      {isEdit && (
        <Typography variant="body-12-regular" color={COLOR.label2[64]}>
          {t('noteEditSpeakerConfiguration')}
        </Typography>
      )}
    </StyledVoicePresetDialog>
  );
};

export default AddSpeakerForm;
